import styles from "./SaveCartModal.module.scss";
import { useState } from "react";
import { RiLockStarLine } from "@remixicon/react";
import { Button, Message, Modal, ModalButtons, Text } from "@eventsquare/uikit";
import {
  Form,
  FormFieldset,
  FormGroup,
  FormInput,
  FormLabel,
  FormSelect,
  FormTextArea,
} from "@eventsquare/uikit/forms";
import { useTranslation } from "react-i18next";

import { ChannelCart } from "@type/channelCart";

import { Api } from "@lib/api";
import { removeCookie } from "@lib/cookies";

interface SaveCartModalProps {
  cart: ChannelCart | null;
  setCartNull: () => void;
  afterSave: () => void;
}

interface SaveFormValues {
  reference: string;
  expires_in: string;
  note: string;
}

interface Error {
  response: {
    body: {
      message: string;
      error: string;
      validation: {
        reference?: string[];
        minutes?: string[];
      };
    };
  };
}

export const SaveCartModal = (props: SaveCartModalProps) => {
  const { cart, setCartNull, afterSave } = props;

  const [errors, setErrors] = useState<string[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  const { t } = useTranslation("components_save_cart_modal");

  const expiresInOptions = [
    { value: "4320", label: t("expire_options.3_days") },
    { value: "10080", label: t("expire_options.1_week") },
    { value: "43800", label: t("expire_options.1_month") },
    { value: "262800", label: t("expire_options.6_months") },
    { value: "525600", label: t("expire_options.1_year") },
    { value: "1576800", label: t("expire_options.3_years") },
  ];

  const initialValues: SaveFormValues = {
    reference: cart?.reference ?? "",
    expires_in: "262800",
    note: "",
  };

  const saveCart = async (values: SaveFormValues) => {
    if (!cart) return;
    try {
      setErrors(null);
      setLoading(true);

      const data: {
        reference: string;
        minutes: string;
        note?: string;
      } = {
        reference: values.reference,
        minutes: values.expires_in,
      };

      if (values.note && values.note.trim().length > 0) {
        data.note = values.note;
      }
      const response = await Api.post(`/cart/${cart.cartid}/save`, data);
      await removeCookie();

      if (response.cart) {
        setSuccess(true);
      }
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (Api.isApiError(error)) {
        console.error(error);
      }

      const err = (error as Error).response.body;
      if (err.error === "validation_failed") {
        const validationErrors: string[] = [];
        for (const [key, value] of Object.entries(err.validation)) {
          value.forEach((v: string) => {
            validationErrors.push(`${key}_${v}`);
          });
        }
        setErrors(validationErrors);
      } else {
        setErrors([err.error ?? "something_went_wrong"]);
      }
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    if (loading) return;
    setErrors(null);
    setCartNull();
  };

  return (
    <Modal
      isVisible={!!cart}
      closeModal={closeModal}
      icon={RiLockStarLine}
      iconColor="info"
    >
      {!success && (
        <div className={styles.saveCartModal}>
          <div className={styles.saveCartModal__info}>
            <Text variant="h3" color="info">
              {cart?.saved_at ? t("title_settings") : t("title_save")}
            </Text>
            {!cart?.saved_at && <Text variant="p">{t("lead")}</Text>}
          </div>

          {errors && errors.length && (
            <Message
              alert
              type="error"
              message={errors.map((error) => t(`errors.${error}`))}
            />
          )}
          <div className={styles.saveCartModal__data}>
            <Form onSubmit={saveCart} initialValues={initialValues}>
              <FormFieldset disabled={loading}>
                <FormGroup>
                  <FormLabel htmlFor="reference">
                    {t("form.reference")}
                  </FormLabel>
                  <FormInput type="text" name="reference" />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="expires_in">
                    {t("form.expires_in")}
                  </FormLabel>
                  <FormSelect
                    name="expires_in"
                    placeholder={t("form.expires_in_placeholder")}
                    options={expiresInOptions}
                  />
                </FormGroup>
                {!cart?.saved_at && (
                  <FormGroup>
                    <FormLabel htmlFor="note">{t("form.note")}</FormLabel>
                    <FormTextArea name="note" />
                  </FormGroup>
                )}
              </FormFieldset>
              <ModalButtons>
                <Button
                  block
                  type="submit"
                  disabled={loading}
                  loading={loading}
                >
                  {t("form.submit")}
                </Button>
                <Button
                  block
                  variant="link"
                  color="subtle"
                  onClick={closeModal}
                  disabled={loading}
                >
                  {t("form.cancel")}
                </Button>
              </ModalButtons>
            </Form>
          </div>
        </div>
      )}

      {success && (
        <>
          <div className={styles.saveCartModal__info}>
            <Text variant="h3">{t("success.title")}</Text>
            <Text variant="p">{t("success.lead")}</Text>
          </div>
          <ModalButtons>
            <Button
              block
              onClick={() => {
                afterSave();
                setCartNull();
              }}
            >
              {t("success.ok")}
            </Button>
          </ModalButtons>
        </>
      )}
    </Modal>
  );
};
